import type { ReactNode } from 'react';

import cn from '../../../../Utils/cn';

export type CardType = 'contained' | 'filled' | 'elevated' | 'elevated-contained';

export interface TitleProps {
  variant: 'XL' | 'LG' | 'SM' | 'XS' | 'XXS';
  weight: 'REGULAR' | 'MEDIUM' | 'SEMI_BOLD';
  colorVariant: 'PRIMARY' | 'SECONDARY' | 'LINK' | 'BUTTON_LINK' | 'DISABLED' | 'BLACK' | 'NOTICE';
}

export interface CardBaseExternalProps {
  title?: string | ReactNode;
  action?: ReactNode;
  direction?: 'left' | 'right';
}

type CardPadding = 15 | 20 | 24;
type CardContentGap = 5 | 10 | 15 | 20;

export interface CardBaseStyleProps {
  className?: string;
  paddingX: CardPadding;
  paddingY: CardPadding;
  shadow?: boolean;
  border?: boolean;
  contentGap: CardContentGap;
  backgroundColor?: string;
  borderColor?: string;
}

const ContentGapToClassMap: Record<CardContentGap, (hasTitle: boolean) => string> = {
  5: (hasTitle: boolean) => cn('gap-4', { 'pt-4': hasTitle }),
  10: (hasTitle: boolean) => cn('gap-12', { 'pt-12': hasTitle }),
  15: (hasTitle: boolean) => cn('gap-16', { 'pt-16': hasTitle }),
  20: (hasTitle: boolean) => cn('gap-20', { 'pt-20': hasTitle }),
};

export const getCardContentContainerSpacingClass = (contentGap: CardContentGap, hasTitle: boolean): string => {
  return ContentGapToClassMap[contentGap](hasTitle);
};

const PaddingXToClassMap: Record<CardPadding, string> = {
  15: 'px-16',
  20: 'px-20',
  24: 'px-24',
};

const PaddingYToClassMap: Record<CardPadding, string> = {
  15: 'py-16',
  20: 'py-20',
  24: 'py-24',
};

export const getCardPaddingClasses = (paddingX: CardPadding, paddingY: CardPadding): string => {
  return `${PaddingXToClassMap[paddingX]} ${PaddingYToClassMap[paddingY]}`;
};

export const CardTypeToStyleAttributesMap: Record<CardType, Partial<CardBaseStyleProps>> = {
  elevated: {
    shadow: true,
    border: false,
    backgroundColor: 'bg-white',
  },
  contained: {
    shadow: false,
    border: true,
    backgroundColor: 'bg-transparent',
  },
  'elevated-contained': {
    shadow: true,
    border: true,
    backgroundColor: 'bg-transparent',
    className: 'border-slate-400',
  },
  filled: {
    shadow: false,
    border: false,
    backgroundColor: 'bg-slate-200',
  },
};
